import { supabase } from "@/integrations/supabase/client";
import { toEdgeFunctionFailure } from "@/utils/userFacingError";
import { Invoice, TransactionSuggestion } from "@/types/payments";
import { CreatePayableRequest, UpdatePayableRequest, Payable } from "@/types/payable";
import { PaymentApiClient } from "@/clients/paymentApiClient";
import { isMoneyTransferEnabled } from "@/config/features";

export class PaymentService {
  private paymentApiClient = new PaymentApiClient();

  /**
   * Fetch all approved invoices (payables)
   */
  public async fetchPayables(): Promise<Invoice[]> {
    try {
      console.log("PaymentService: Attempting to fetch payables");
      const { data, error } = await supabase.functions.invoke("payment-api", {
        body: { action: "getPayables" }
      });
      
      if (error) {
        console.error("Error fetching payables:", error);
        throw await toEdgeFunctionFailure(error, 'Error fetching payables');
      }
      
      console.log("PaymentService: Payables fetch successful", data);
      return data as Invoice[];
    } catch (error) {
      console.error("Failed to fetch payables:", error);
      throw error;
    }
  }

  /**
   * Get pending payables balance in specified currency
   */
  public async getPendingPayablesBalance(targetCurrency: string = 'USD'): Promise<{ balance: number, currency: string }> {
    try {
      const { data, error } = await supabase.functions.invoke("payment-api", {
        body: {
          action: "getPendingPayablesBalance",
          currency: targetCurrency
        }
      });
      if (error) throw await toEdgeFunctionFailure(error, 'Error fetching pending payables balance');
      return data;
    } catch (error) {
      console.error("Failed to fetch pending payables balance:", error);
      throw error;
    }
  }

  /**
   * Create a new payable record
   */
  public async createPayable(request: CreatePayableRequest): Promise<Payable> {
    try {
      console.log("PaymentService: Creating payable", request);
      const { data, error } = await supabase.functions.invoke("payment-api", {
        body: {
          action: "createPayable",
          ...request
        }
      });
      
      if (error) {
        console.error("Error creating payable:", error);
        throw await toEdgeFunctionFailure(error, 'Error creating payable');
      }
      
      console.log("PaymentService: Payable created successfully", data);
      return data as Payable;
    } catch (error) {
      console.error("Failed to create payable:", error);
      throw error;
    }
  }

  /**
   * Update an existing payable record
   * Can update by either payable ID or invoice ID
   */
  public async updatePayable(request: UpdatePayableRequest): Promise<Payable> {
    if (!request.id && !request.invoice_id) {
      throw new Error("Either id or invoice_id must be provided to update a payable");
    }
    // PaymentApiClient surfaces the server's named refusals (e.g. a pay-from account change
    // refused while a payment is in progress, FIN-968) instead of the SDK's generic message.
    return this.paymentApiClient.updatePayable(request);
  }

  /**
   * Update payable status (for manual handling, etc.)
   */
  public async updatePayableStatus(payableId: string, status: string, paymentDate?: string, isInvoiceId: boolean = false, actualSekAmount?: number): Promise<Payable> {
    console.log("PaymentService: updatePayableStatus called", { payableId, status, paymentDate, isInvoiceId, actualSekAmount });
    const result = await this.paymentApiClient.updatePayableStatus(payableId, status, paymentDate, isInvoiceId, actualSekAmount);
    console.log("PaymentService: updatePayableStatus completed", result);
    return result;
  }

  /**
   * Precomputed payable→transaction match suggestions (issue #462). See
   * PaymentApiClient.getPrecomputedTransactionSuggestionsForPayable for the "no JIT fallback"
   * rationale.
   */
  public async getPrecomputedTransactionSuggestionsForPayable(payableId: string): Promise<TransactionSuggestion[]> {
    return this.paymentApiClient.getPrecomputedTransactionSuggestionsForPayable(payableId);
  }

  /**
   * Confirm a manual payable→transaction match (issue #462).
   */
  public async confirmPayableMatch(payableId: string, transactionId: string): Promise<{ decisionId: string }> {
    return this.paymentApiClient.confirmPayableMatch(payableId, transactionId);
  }

  /**
   * Acknowledge a payable as paid externally when a bank account is connected but no matching
   * transaction was found, or the consultant explicitly ruled out every suggested candidate
   * (issue #498, design plan's case c / "None of these match" escape hatch).
   */
  public async acknowledgeExternalPayment(payableId: string, paymentDate: string, actualSekAmount?: number): Promise<{ decisionId: string }> {
    return this.paymentApiClient.acknowledgeExternalPayment(payableId, paymentDate, actualSekAmount);
  }

  /**
   * Pay selected invoices by their IDs
   */
  public async paySelected(payableIds: string[], sameDevice: boolean = true): Promise<{ url?: string, signingBasketId: string, authorisationId: string, authMethod?: string, qrCodeImage?: string, qrToken?: string, bankIdUrl?: string, scaRedirect?: string, refreshInterval?: number, bicFi: string }> {
    return this.paymentApiClient.paySelected(payableIds, sameDevice);
  }

  /**
   * Get authorisation status for a payment
   */
  public async getAuthorisationStatus(
    signingBasketId: string,
    authorisationId: string,
    xRequestId: string,
    bicFi: string,
    signingFor: 'payables' | 'transfer' = 'payables',
  ): Promise<{ completed: boolean, needsReview?: boolean, message?: string }> {
    return this.paymentApiClient.getAuthorisationStatus(signingBasketId, authorisationId, xRequestId, bicFi, signingFor);
  }

  /**
   * Transfer money between accounts
   */
  public async transferMoney({ fromAccountId, toAccountId, amount, currency }: { fromAccountId: string, toAccountId: string, amount: number, currency: string }): Promise<{ url: string, signingBasketId: string, authorisationId: string }> {
    if (!isMoneyTransferEnabled()) {
      throw new Error("Money transfer feature is not available");
    }
    return this.paymentApiClient.transferMoney({ fromAccountId, toAccountId, amount, currency });
  }
}
